import React, { useState, useMemo } from 'react';
import Header from 'components/ui/Header';
import Breadcrumb from 'components/ui/Breadcrumb';
import ContactQuickAccess from 'components/ui/ContactQuickAccess';
import Icon from 'components/AppIcon';

import ServiceCard from './components/ServiceCard';
import FilterBar from './components/FilterBar';
import PackageComparison from './components/PackageComparison';

const ServicesCatalog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [frequency, setFrequency] = useState('all');

  const services = [
    {
      id: 1,
      title: "Mantenimiento Regular",
      titleEn: "Regular Maintenance",
      category: "maintenance",
      image: "https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg",
      description: `Servicio completo de mantenimiento semanal que incluye limpieza de superficie, aspirado del fondo, balanceado químico del agua, limpieza de filtros y revisión de equipos. Mantenemos su piscina en condiciones óptimas durante todo el año con productos de alta calidad y técnicos certificados.`,
      duration: "2-3 horas",
      priceColones: 25000,
      priceDollars: 45,
      frequency: "weekly",
      features: [
        "Limpieza de superficie y paredes",
        "Aspirado completo del fondo",
        "Balanceado químico del agua",
        "Limpieza y mantenimiento de filtros",
        "Revisión de equipos de bombeo",
        "Reporte detallado del estado"
      ],
      popular: true
    },
    {
      id: 2,
      title: "Limpieza Profunda",
      titleEn: "Deep Cleaning",
      category: "deep-cleaning",
      image: "https://images.pexels.com/photos/1001682/pexels-photo-1001682.jpeg",
      description: `Limpieza intensiva para piscinas que han estado sin mantenimiento o requieren restauración completa. Incluye remoción de algas, limpieza de azulejos, tratamiento de manchas difíciles y restauración completa del equilibrio químico del agua.`,
      duration: "4-6 horas",
      priceColones: 45000,
      priceDollars: 80,
      frequency: "one-time",
      features: [
        "Remoción completa de algas y sedimentos",
        "Limpieza profunda de azulejos y juntas",
        "Tratamiento de manchas y depósitos",
        "Vaciado y llenado si es necesario",
        "Restauración química completa",
        "Desinfección total del sistema"
      ],
      popular: false
    },
    {
      id: 3,
      title: "Reparación de Equipos",
      titleEn: "Equipment Repair",
      category: "repair",
      image: "https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg",
      description: `Servicio especializado en diagnóstico y reparación de equipos de piscina incluyendo bombas, filtros, calentadores, sistemas de cloración y automatización. Técnicos certificados con garantía en todas las reparaciones realizadas.`,
      duration: "1-4 horas",
      priceColones: 35000,
      priceDollars: 65,
      frequency: "as-needed",
      features: [
        "Diagnóstico completo de equipos",
        "Reparación de bombas y motores",
        "Mantenimiento de sistemas de filtración",
        "Reparación de calentadores",
        "Instalación de nuevos equipos",
        "Garantía de 6 meses en reparaciones"
      ],
      popular: false
    },
    {
      id: 4,
      title: "Paquete Estacional",
      titleEn: "Seasonal Package",
      category: "seasonal",
      image: "https://images.pexels.com/photos/1166644/pexels-photo-1166644.jpeg",
      description: `Paquete completo para preparación estacional de su piscina. Incluye apertura o cierre de temporada, tratamiento preventivo, revisión completa de equipos y programa de mantenimiento adaptado a las condiciones climáticas de Costa Rica.`,
      duration: "3-5 horas",
      priceColones: 55000,
      priceDollars: 100,
      frequency: "seasonal",
      features: [
        "Preparación de apertura/cierre",
        "Tratamiento preventivo completo",
        "Revisión exhaustiva de equipos",
        "Programa de mantenimiento estacional",
        "Productos químicos incluidos",
        "Seguimiento durante la temporada"
      ],
      popular: false
    },
    {
      id: 5,
      title: "Mantenimiento Quincenal",
      titleEn: "Bi-weekly Maintenance",
      category: "maintenance",
      image: "https://images.pexels.com/photos/1001682/pexels-photo-1001682.jpeg",
      description: `Servicio de mantenimiento cada dos semanas ideal para piscinas de uso moderado. Incluye limpieza básica, balanceado químico y revisión de equipos con la calidad y profesionalismo que nos caracteriza.`,
      duration: "2-3 horas",
      priceColones: 18000,
      priceDollars: 32,
      frequency: "bi-weekly",
      features: [
        "Limpieza de superficie",
        "Aspirado del fondo",
        "Balanceado químico básico",
        "Limpieza de skimmers",
        "Revisión visual de equipos",
        "Reporte de estado"
      ],
      popular: false
    },
    {
      id: 6,
      title: "Servicio de Emergencia",
      titleEn: "Emergency Service",
      category: "emergency",
      image: "https://images.pexels.com/photos/2219024/pexels-photo-2219024.jpeg",
      description: `Servicio de emergencia 24/7 para problemas urgentes como agua verde, fallas de equipos críticos o preparación rápida para eventos especiales. Respuesta garantizada en menos de 4 horas en el área metropolitana.`,
      duration: "1-3 horas",
      priceColones: 40000,
      priceDollars: 75,
      frequency: "emergency",
      features: [
        "Respuesta en menos de 4 horas",
        "Disponible 24/7",
        "Tratamiento de emergencia",
        "Reparaciones urgentes",
        "Preparación para eventos",
        "Sin costo adicional por urgencia"
      ],
      popular: false
    }
  ];

  const categories = [
    { id: 'all', label: 'Todos los Servicios', labelEn: 'All Services' },
    { id: 'maintenance', label: 'Mantenimiento', labelEn: 'Maintenance' },
    { id: 'deep-cleaning', label: 'Limpieza Profunda', labelEn: 'Deep Cleaning' },
    { id: 'repair', label: 'Reparaciones', labelEn: 'Repairs' },
    { id: 'seasonal', label: 'Estacional', labelEn: 'Seasonal' },
    { id: 'emergency', label: 'Emergencia', labelEn: 'Emergency' }
  ];

  const priceRanges = [
    { id: 'all', label: 'Todos los Precios', labelEn: 'All Prices' },
    { id: 'low', label: 'Hasta ₡25,000', labelEn: 'Up to $45' },
    { id: 'medium', label: '₡25,000 - ₡40,000', labelEn: '$45 - $75' },
    { id: 'high', label: 'Más de ₡40,000', labelEn: 'Over $75' }
  ];

  const frequencies = [
    { id: 'all', label: 'Todas las Frecuencias', labelEn: 'All Frequencies' },
    { id: 'weekly', label: 'Semanal', labelEn: 'Weekly' },
    { id: 'bi-weekly', label: 'Quincenal', labelEn: 'Bi-weekly' },
    { id: 'one-time', label: 'Una vez', labelEn: 'One-time' },
    { id: 'seasonal', label: 'Estacional', labelEn: 'Seasonal' },
    { id: 'emergency', label: 'Emergencia', labelEn: 'Emergency' }
  ];

  const filteredServices = useMemo(() => {
    return services.filter(service => {
      const categoryMatch = selectedCategory === 'all' || service.category === selectedCategory;
      
      let priceMatch = true;
      if (priceRange === 'low') {
        priceMatch = service.priceColones <= 25000;
      } else if (priceRange === 'medium') {
        priceMatch = service.priceColones > 25000 && service.priceColones <= 40000;
      } else if (priceRange === 'high') {
        priceMatch = service.priceColones > 40000;
      }
      
      const frequencyMatch = frequency === 'all' || service.frequency === frequency;
      
      return categoryMatch && priceMatch && frequencyMatch;
    });
  }, [selectedCategory, priceRange, frequency, services]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-4">
              Catálogo de Servicios
            </h1>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Descubre nuestros servicios profesionales de limpieza y mantenimiento de piscinas. 
              Calidad garantizada con más de 10 años de experiencia en Costa Rica.
            </p>
          </div>

          {/* Filter Bar */}
          <FilterBar
            categories={categories}
            priceRanges={priceRanges}
            frequencies={frequencies}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            frequency={frequency}
            setFrequency={setFrequency}
          />

          {/* Results Count */}
          <div className="mb-8">
            <p className="text-text-secondary">
              Mostrando {filteredServices.length} de {services.length} servicios
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>

          {/* No Results */}
          {filteredServices.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Search" size={48} className="text-primary" />
              </div>
              <h3 className="text-2xl font-heading font-semibold text-text-primary mb-4">
                No se encontraron servicios
              </h3>
              <p className="text-text-secondary mb-8">
                Intenta ajustar los filtros para encontrar el servicio que necesitas.
              </p>
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setPriceRange('all');
                  setFrequency('all');
                }}
                className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 nav-transition"
              >
                Limpiar Filtros
              </button>
            </div>
          )}

          {/* Package Comparison */}
          <PackageComparison />

          {/* Call to Action */}
          <div className="bg-primary/5 rounded-lg p-8 md:p-12 text-center">
            <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
              ¿Necesitas un Servicio Personalizado?
            </h2>
            <p className="text-xl text-text-secondary mb-8 max-w-2xl mx-auto">
              Contáctanos para crear un plan de mantenimiento adaptado a las necesidades específicas de tu piscina.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact-quote-request"
                className="bg-primary text-white px-8 py-4 rounded-lg font-medium hover:bg-primary/90 nav-transition inline-flex items-center justify-center space-x-2"
              >
                <Icon name="MessageSquare" size={20} />
                <span>Solicitar Cotización</span>
              </a>
              <a
                href="tel:+50688888888"
                className="bg-surface border-2 border-primary text-primary px-8 py-4 rounded-lg font-medium hover:bg-primary/5 nav-transition inline-flex items-center justify-center space-x-2"
              >
                <Icon name="Phone" size={20} />
                <span>Llamar Ahora</span>
              </a>
            </div>
          </div>
        </div>
      </main>

      <ContactQuickAccess />
    </div>
  );
};

export default ServicesCatalog;